import React, { useEffect, useState } from "react";
import Layout from "../../../components/template/Layout";
import WorkOrderCard from "../../../components/template/Card/WorkOrderCard";
import { FiSearch } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchWorkOrders } from "../../../redux/slices/workOrderSlice";
import { WorkOrderStatus } from "../../../types/workOrder";
import PullToRefresh from "react-simple-pull-to-refresh";
import { isReallyOnline } from "../../../lib/isOnline";
import DeveloperSettingsModal from "../../../components/template/Modal/DeveloperSettingsModal";
import { isOfflineMode } from "../../../shared/offlineManager";

const isLowerEnvironment =
  import.meta.env.VITE_APP_ENVIRONMENT === "dev" ||
  import.meta.env.VITE_APP_ENVIRONMENT === "qa";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<WorkOrderStatus>(
    WorkOrderStatus.InProgress
  );

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [showDevSettingsModal, setShowDevSettingsModal] = useState(false); // State for modal

  const { workOrders, loading } = useSelector(
    (state: RootState) => state.workOrders
  );
  const token = useSelector((state: RootState) => state.auth?.token);

  const scheduledWorkOrders = workOrders?.filter(
    (work_order) => work_order.workOrderStatus === WorkOrderStatus.Scheduled
  );

  const inProgressWorkOrders = workOrders?.filter(
    (work_order) => work_order.workOrderStatus === WorkOrderStatus.InProgress 
  );

  const completedWorkOrders = workOrders?.filter(
    (work_order) => work_order.workOrderStatus === WorkOrderStatus.Completed
  );

  const filteredWorkOrders =
    activeTab === WorkOrderStatus.Scheduled
      ? scheduledWorkOrders
      : activeTab === WorkOrderStatus.InProgress
      ? inProgressWorkOrders
      : completedWorkOrders;

  const searchedWorkOrders = filteredWorkOrders?.filter(
    (work_order) =>
      work_order.accountName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      work_order.package_Name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      work_order.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(work_order.workOrder_ID).includes(searchTerm)
  );

  const getWorkOrders = async () => {
    if (!isOfflineMode()) {
      if (token) dispatch(fetchWorkOrders(token));
    }
  };

  useEffect(() => {
    if (isLowerEnvironment) {
      const showDevSettingsModal = localStorage.getItem("showDevSettingsModal");
      if (showDevSettingsModal !== "no") {
        setShowDevSettingsModal(true);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const checkOnlineStatus = async () => {
      const onlineStatus = await isReallyOnline();
      if (isMounted) {
        setIsOnline(onlineStatus);
      }
    };
    const interval = setInterval(checkOnlineStatus, 1000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    getWorkOrders();
  }, [token]);

  const handleConfirmDevSettings = async (settings: {
    compressionEnabled: string;
    batchSize: string;
  }) => {
    await localStorage.setItem("compressionEnabled", settings.compressionEnabled);
    await localStorage.setItem("batchSize", settings.batchSize);
    await localStorage.setItem("showDevSettingsModal", "no");
    setShowDevSettingsModal(false);
  };

  return (
    <PullToRefresh
      onRefresh={getWorkOrders}
      refreshingContent={
        <div className="flex justify-center items-center mt-4">
          <div className="w-8 h-8 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
        </div>
      }
    >
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Layout title={<div>Work Orders</div>} isDarkMode={true}>
          <div className="bg-primary text-white flex justify-around pb-5">
            <button
              className={`font-semibold pb-1 ${
                activeTab === WorkOrderStatus.Scheduled
                  ? "border-b-2 border-white text-white"
                  : "text-success"
              }`}
              onClick={() => setActiveTab(WorkOrderStatus.Scheduled)}
            >
              Scheduled ({scheduledWorkOrders?.length})
            </button>
            <button
              className={`font-semibold pb-1 ${
                activeTab === WorkOrderStatus.InProgress
                  ? "border-b-2 border-white text-white"
                  : "text-success"
              }`}
              onClick={() => setActiveTab(WorkOrderStatus.InProgress)}
            >
              In Progress ({inProgressWorkOrders?.length})
            </button>
            <button
              className={`font-semibold pb-1 ${
                activeTab === WorkOrderStatus.Completed
                  ? "border-b-2 border-white text-white"
                  : "text-success"
              }`}
              onClick={() => setActiveTab(WorkOrderStatus.Completed)}
            >
              Completed ({completedWorkOrders?.length})
            </button>
          </div>

          <div className="bg-primary">
            <div className="p-4 py-6 flex flex-col flex-grow rounded-t-[2rem] bg-gray-100">
              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-3 pr-10 shadow-bar-shadow rounded-lg"
                  placeholder="Search..."
                />
                <FiSearch className="h-5 w-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              <div className="pb-4">
                <div className="flex-grow space-y-4 h-[calc(100vh-300px)] overflow-y-auto hide-scrollbar">
                  {loading && searchedWorkOrders?.length == 0 ? (
                    <div className="flex justify-center items-center">
                      <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin"></div>
                    </div>
                  ) : searchedWorkOrders?.length > 0 ? (
                    searchedWorkOrders.map((work_order) => (
                      <WorkOrderCard
                        key={work_order.workOrder_ID}
                        id={work_order.workOrder_ID}
                        order={work_order}
                        isOnline={isOnline}
                      />
                    ))
                  ) : (
                    <p className="text-center text-gray-500">
                      No work orders found for "{activeTab}"
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isLowerEnvironment && (
            <DeveloperSettingsModal
              isOpen={showDevSettingsModal}
              onClose={async () => {
                setShowDevSettingsModal(false);
                await localStorage.setItem("showDevSettingsModal", "no");
              }}
              onConfirm={handleConfirmDevSettings}
            />
          )}
        </Layout>
      </div>
    </PullToRefresh>
  );
};

export default Dashboard;
